import createReducer from 'data/shared/createReducer';
import {map as actions, book} from 'data/booking/constants/action_types';
import without from "lodash/without";
import last from "lodash/last";
import first from "lodash/first";
import {MAP_LAYER_IGN, MAP_OUTDOOR} from "data/booking/constants/map_filters_type";
import {isEmpty} from "lodash";
import omit from "lodash/omit";
import {BAG_TRANSPORT, CAR_PARKING, CAR_TRANSPORT} from "data/booking/constants/service_types";
import reverse from "lodash/reverse";
import {getDefaultStepId} from "data/booking/selectors/car_parking_form";

const tripInitialState = {
    serviceId: null,
    itemId: null,
    serviceType: null,
    hike: null,
    steps: [],
    selectedStepIndex: null,
    selectedStep: null,
    selectedStop: null,
    startStep: {
        stop: null,
        step: null
    },
    endStep: {
        stop: null,
        step: null
    }
}

const initialState = {
    bounds: {
        corner1: [50.631419, -6.959795],
        corner2: [41.767551, 13.058639]
    },
    mapLayer: MAP_OUTDOOR,
    isFetching: false,
    isUpdating: false,
    isFetchingPlanSteps: false,
    isFetchingThemes: false,
    stops: [],
    moseOverStopId: null,
    kmlData: {},
    filters : [],
    showSummary: false,
    showThemes: true,
    showStops: false,
    hikerMarkerCoordinates: null,
    hike: null,
    theme: null,
    moseOverHike: null,
    loadingSteps: [],
    entities: {
        services: {},
        hikes: {},
        themes: {},
        items:{}
    },
    direction: 1,
    trip: tripInitialState,
}

const requestStops = (state, action) => {

    return {
        ...state,
        isFetching: true,
    }
};

const requestPlanSteps = (state, action) => {

    return {
        ...state,
        isFetchingPlanSteps: true,
    }
};

const receivePlanSteps = (state, action) => {
    let newSteps = []
    for(let i = 0; i<action.steps.length -1 ; i++){
        newSteps.push({
            from: {
                stop: null,
                step: action.steps[i]
            },
            to: {
                stop: null,
                step: action.steps[i+1]
            }
        })
    }

    return {
        ...state,
        isFetchingPlanSteps: false,
        trip:{
            ...state.trip,
            steps: newSteps
        }
    }
};

const receiveStops = (state, action) => ({
    ...state,
    stops: state.showThemes == false && state.trip.selectedStep ? action.stops : [],
    isFetching:false
});

const moseOverStop = (state, action) => ({
    ...state,
    moseOverStopId: action.stopId
});

const toggleSummary = (state, action) => {
    const startStep = !state.showSummary ? first(state.trip.steps).from : last(state.trip.steps).to
    const endStep = !state.showSummary ? last(state.trip.steps).to : {
        stop: null,
        step: null
    }

    return {
        ...state,
        bounds: {
            corner1: [startStep.step.latitude, startStep.step.longitude],
            corner2: endStep.step ? [endStep.step.latitude, endStep.step.longitude] : null
        },
        showSummary: !state.showSummary,
        stops: [],
        trip: {
            ...state.trip,
            selectedStep: null,
            selectedStop: null,
            selectedStepIndex: null,
            startStep,
            endStep,
        }
    }
};

const selectDepartureStep = (state, action) => {
    if(action.step == null){
        return {
            ...state,
            trip: {
                ...state.trip,
                startStep: {
                    stop: null,
                    step: null
                },
                endStep: {
                    stop: null,
                    step: null
                },
                selectedStep: null
            }
        }
    }
    if(state.entities.steps) {
        const step = state.entities.steps[action.step.id]
        return {
            ...state,
            bounds: {
                corner1: !isEmpty(step) ? [step.latitude, step.longitude] : state.bounds.corner1,
                corner2: !isEmpty(step) ? null : state.bounds.corner2
            },
            trip: {
                ...state.trip,
                startStep: {
                    stop: action.stop ? action.stop : null,
                    step: step
                },
                selectedStep: step
            }
        }
    }
    return state
};

const selectArrivalStep = (state, action) => {
    if(action.step == null){
        return {
            ...state,
            trip: {
                ...state.trip,
                endStep: {
                    stop: null,
                    step: null
                },
                selectedStep: null
            }
        }
    }
    const step = action.step.id && state.entities.steps ? state.entities.steps[action.step.id] : action.step

    return {
        ...state,
        bounds: !isEmpty(step) ? {
            corner1: [step.latitude, step.longitude],
            corner2: null
        } : state.bounds,
        trip: {
            ...state.trip,
            endStep: {
                stop: action.stop ? action.stop : null,
                step
            },
            selectedStep: step
        }
    }
};

const setKmlData = (state, action) => ({
    ...state,
    kmlData: {
        ...state.kmlData,
        [action.hikeId]: action.data
    }
});

const updateFilters = (state, action) => ({
    ...state,
    filters: state.filters.includes(action.filter) ? without(state.filters, action.filter) : [...state.filters, action.filter]
})

const saveStep = (state, action) => {

    const service = state.entities.services[state.trip.serviceId]

    if(!state.trip.hike){
        return state
    }

    let newSteps = [...state.trip.steps]

    if (state.trip.selectedStepIndex !== null) {
        // current step
        newSteps[state.trip.selectedStepIndex] = {
            ...newSteps[state.trip.selectedStepIndex],
            from: state.trip.startStep,
            to: state.trip.endStep,
            updateStatus: state.isUpdating ? 'edited' : null
        }
        //next step
        if(service.type == BAG_TRANSPORT) {
            const oldDistance = state.trip.steps[state.trip.selectedStepIndex].to.step.distance
            const newDistance = state.trip.endStep && state.trip.endStep.step ? state.trip.endStep.step.distance : 0
            const distanceDiff = Math.abs(oldDistance - newDistance)

            if (state.trip.selectedStepIndex + 1 < state.trip.steps.length) {
                let nextStepDistance = newSteps[state.trip.selectedStepIndex + 1].to.step.distance
                newSteps[state.trip.selectedStepIndex + 1] = {
                    ...newSteps[state.trip.selectedStepIndex + 1],
                    from: state.trip.endStep,
                    to: {
                        ...newSteps[state.trip.selectedStepIndex + 1].to,
                        step: {
                            ...newSteps[state.trip.selectedStepIndex + 1].to.step,
                            distance: oldDistance > newDistance ? nextStepDistance + distanceDiff : nextStepDistance - distanceDiff
                        }
                    },
                    updateStatus: state.isUpdating ? 'added' : null
                }
            }

        }
    } else {
        newSteps.push({
            itemId: action.itemId,
            from: state.trip.startStep,
            to: state.trip.endStep,
            updateStatus: state.isUpdating ? 'added' : null
        })
    }

    let tripInfos = {
        ...resetBounds(state, action),
        trip: tripInitialState,
        showStops: false,
        showThemes: true
    }
    // dans le cas du transport de bagage, on continue la saisie d'étape
    if(service.type == BAG_TRANSPORT){
        const startStep = last(newSteps).to
        if(startStep.step) {
            tripInfos = {
                bounds: {
                    corner1: [startStep.step.latitude, startStep.step.longitude],
                    corner2: null
                },
                trip: {
                    ...state.trip,
                    steps: newSteps,
                    selectedStep: null,
                    selectedStop: null,
                    selectedStepIndex: null,
                    startStep,
                    endStep: {
                        stop: null,
                        step: null
                    },
                },
                showThemes: false
            }
        }
    }

    return {
        ...state,
        ...tripInfos,
        hike: service.type == BAG_TRANSPORT ? state.hike : null,
        entities: {
            ...state.entities,
            services: {
                ...state.entities.services,
                [action.serviceId]: {
                    ...state.entities.services[action.serviceId],
                    steps: newSteps
                }
            }
        }
    }
};

const newStep = (state, action) => {
    const startStep = last(state.trip.steps).to
    return {
        ...state,
        bounds: {
            corner1: [startStep.step.latitude, startStep.step.longitude],
            corner2: null
        },
        trip: {
            ...state.trip,
            selectedStep: null,
            selectedStop: null,
            selectedStepIndex: null,
            startStep,
            endStep: {
                stop: null,
                step: null
            },
        },
        stops: [],
        showStops: false
    }
};

const deleteStep = (state, action) => {

    let newSteps = [...state.trip.steps]
    if(action.stepIndex > 0 && action.stepIndex < state.trip.steps.length - 1){
        let distance = newSteps[action.stepIndex].to.step.distance + newSteps[action.stepIndex +1].to.step.distance
        newSteps[action.stepIndex+1].from = newSteps[action.stepIndex].from
        newSteps[action.stepIndex+1].to.step.distance = distance
    }
    newSteps.splice(action.stepIndex, 1)

    return {
        ...state,
        trip: {
            ...state.trip,
            steps: newSteps,
            selectedStep: null,
            selectedStop: null,
            selectedStepIndex: null,
            startStep: {
                stop: null,
                step: null
            },
            endStep: {
                stop: null,
                step: null
            },
        },
        stops: []
    }
};

const saveStop = (state, action) => {

    return {
        ...state,
        trip: {
            ...state.trip,
            selectedStep: null,
            selectedStop: null,
            startStep: {
                ...state.trip.startStep,
                stop: state.trip.startStep.step.id == state.trip.selectedStep.id ? action.stop : state.trip.startStep.stop,
            },
            endStep: {
                ...state.trip.endStep,
                stop: state.trip.endStep.step && state.trip.endStep.step.id == state.trip.selectedStep.id ? action.stop : state.trip.endStep.stop,
            },
        },
        stops: [],
        showStops: false
    }
};

const saveService = (state, action) => {
    let service = {
        id: action.data.id,
        type: action.data.type,
        hike: state.trip.hike,
        steps: state.trip.steps
    }

    let services = {...state.entities.services};
    services[action.data.id] = service
    return {
        ...state,
        trip: {
            ...state.trip,
            serviceId: action.data.id
        },
        entities: {
            ...state.entities,
            services
        }
    }
}

const saveServiceType = (state, action) => ({
    ...state,
    trip: {
        ...state.trip,
        serviceType: action.serviceType,
    }
});

const editStep = (state, action) => {
    let steps = state.trip.steps
    let hike = state.trip.hike
    let serviceId = action.serviceId
    let itemId = action.itemId
    if(state.trip.serviceId == null || state.trip.serviceId != serviceId){
        const service = state.entities.services[action.serviceId]
        steps = service.steps
        hike = service.hike.id ? service.hike : state.entities.hikes[service.hike]
    }

    if(steps.length == 0 || (state.trip.serviceId == serviceId && state.trip.itemId == itemId)){
        return cancelTripEdit(state, action)
    }
    const {from, to} = steps[action.stepIndex]

    if(from && to) {
        return {
            ...state,
            showThemes: false,
            hike,
            bounds: {
                corner1: from.step ? [from.step.latitude, from.step.longitude] : state.bounds.corner1,
                corner2: to.step? [to.step.latitude, to.step.longitude] : state.bounds.corner2
            },
            trip: {
                ...state.trip,
                hike,
                serviceId,
                itemId,
                steps,
                selectedStepIndex: action.stepIndex,
                selectedStep: null,
                selectedStop: null,
                startStep: from,
                endStep: to,
            }
        }
    }
    return {
        ...state,
        showThemes: false,
        hike,
        trip: {
            ...state.trip,
            hike,
            serviceId,
            itemId,
            steps,
            selectedStepIndex: action.stepIndex,
            selectedStep: null,
            selectedStop: null,
            startStep: from,
            endStep: to,
        }
    }
};

const addStep = (state, action) => {
    let steps = state.trip.steps
    let hike = state.trip.hike
    let serviceId = action.serviceId

    if(state.trip.serviceId == null || state.trip.serviceId != serviceId){
        const service = state.entities.services[action.serviceId]
        hike = service.hike.id ? service.hike : state.entities.hikes[service.hike]
        steps = service.steps
    }

    if(steps.length == 0){
        return state
    }

    const startStep = last(steps).to
    return {
        ...state,
        bounds: {
            corner1: [startStep.step.latitude, startStep.step.longitude],
            corner2: null
        },
        trip: {
            ...state.trip,
            hike,
            serviceId,
            selectedStep: null,
            selectedStop: null,
            selectedStepIndex: null,
            startStep,
            endStep: {
                stop: null,
                step: null
            },
            steps
        },
        stops: [],
        showStops: false,
        showThemes: false,
    }
};

const addItem = (state, action) => ({
    ...state,
    trip: {
        ...state.trip,
        itemId: null,
    },
    hike : state.trip.hike ? state.trip.hike : state.hike
});

const selectStep = (state, action) => ({
    ...state,
    bounds: {
        corner1: action.step ? [action.step.latitude, action.step.longitude] : state.bounds.corner1,
        corner2: null
    },
    trip: {
        ...state.trip,
        selectedStep: action.step,
        selectedStop: null
    }
});

const selectStop = (state, action) => ({
    ...state,
    showStops: false,
    trip: {
        ...state.trip,
        selectedStep: action.step ? state.entities.steps[action.step.id] : state.trip.selectedStep,
        selectedStop: action.stop,
    }
});

const moveHikerMarker = (state, action) => ({
    ...state,
    hikerMarkerCoordinates: action.coordinates
});

const selectHike = (state, action) => {

    if(!action.hike){
        return {
            ...state,
            hike: action.hike,
            theme: action.theme
        }
    }

    let hike = state.entities.hikes[action.hike.id] ? state.entities.hikes[action.hike.id] : action.hike

    return {
        ...state,
        hike,
        theme: action.theme,
        entities: {
            ...state.entities,
            hikes: {
                ...state.entities.hikes,
                [action.hike.id]: hike
            }
        }
    }
};

const clearTheme = (state, action) => {
    const {hikes} = state.entities
    const hike = state.hike ? state.hike : action.data.hike.id ? action.data.hike : hikes[action.data.hike]

    return {
        ...state,
        hike,
        theme: null,
        showThemes:false,
        trip:{
            ...state.trip,
            hike
        }
    }
}

const receiveHikeSteps = (state, action) => {
    if(action.steps) {

        let items = {...state.entities.items}

        Object.keys(state.entities.services).forEach(serviceId => {
            const service =  state.entities.services[serviceId]
            let hikeId = service.hike?.id ?? service.hike
            if(hikeId == action.hikeId && service.type == BAG_TRANSPORT){
                if(!isEmpty(service.items)) {
                    service.items.forEach(itemId => {
                        let item = {
                            ...state.entities.items[itemId]
                        }

                        if(!isEmpty(action.steps.entities.steps)) {
                            if (!isEmpty(action.steps.entities.steps[item.from.step.id])) {
                                item.from.step = {
                                    ...item.from.step,
                                    ...action.steps.entities.steps[item.from.step.id]
                                }
                            }
                            if (!isEmpty(action.steps.entities.steps[item.to.step.id])) {
                                item.to.step = {
                                    ...item.to.step,
                                    ...action.steps.entities.steps[item.to.step.id]
                                }
                            }
                        }
                        items[itemId] = item
                    })
                }
            }
        })

        return {
            ...state,
            loadingSteps: without( state.loadingSteps, action.hikeId ),
            entities: {
                ...state.entities,
                steps: {
                    ...state.entities.steps,
                    ...action.steps.entities.steps
                },
                hikes: {
                    ...state.entities.hikes,
                    [action.hikeId]: {
                        ...state.entities.hikes[action.hikeId],
                        steps: action.steps.result
                    }
                },
                items
            }
        }
    }
    return {
        ...state,
        loadingSteps: without( state.loadingSteps, action.hikeId )
    };
};

const requestHikeSteps = (state, action) => {
    return {
        ...state,
        loadingSteps: !state.loadingSteps.includes(action.hikeId) ? state.loadingSteps.concat([action.hikeId]) : state.loadingSteps,
    }
};

const moseOverHike = (state, action) => ({
    ...state,
    moseOverHike: action.hike,
});

const requestThemes = (state, action) => ({
    ...state,
    isFetchingThemes: true,
});

const receiveThemes = (state, action) => ({
    ...state,
    isFetchingThemes: false,
    entities: {
        ...state.entities,
        themes: action.data.entities.themes,
        hikes: {
            ...action.data.entities.hikes,
            ...state.entities.hikes
        }
    },
});

const receiveHikes = (state, action) => ({
    ...state,
    entities: {
        ...state.entities,
        hikes: action.data.entities.hikes
    },
});

const selectTheme = (state, action) => {
    const {northWest, southEast} = action.theme ? action.theme.coordinates : {}

    return {
        ...state,
        bounds: {
            corner1: northWest ? [northWest.latitude, northWest.longitude] : initialState.bounds.corner1,
            corner2: southEast ? [southEast.latitude, southEast.longitude] : initialState.bounds.corner2
        },
        theme: action.theme,
        hike: null,
    }
}

const startTripEdit = (state, action) => ({
    ...state,
    showThemes: false,
    trip: {
        ...state.trip,
        hike: state.hike
    }
});

const cancelTripEdit = (state, action) => ({
    ...resetBounds(state, action),
    showThemes: true,
    stops: [],
    showStops: false,
    trip: tripInitialState
});

const changeMapLayer = (state, action) => ({
    ...state,
    mapLayer: action.mapLayer
});

const updateBounds = (state, action) => ({
    ...state,
    bounds: {
        corner1 : action.corner1,
        corner2 : action.corner2
    }
});

const resetBounds = (state, action) => {

    let hikes = {}

    Object.keys(state.entities.services).forEach(function(serviceId){
        const service = state.entities.services[serviceId]
        if(service.hike) {
            hikes[service.hike.id] = service.hike.id
        }
    })

    let bounds = {...state.bounds};

    if(isEmpty(hikes) && !isEmpty(state.hike)){
        hikes[state.hike.id] = state.hike
    }

    Object.keys(hikes).forEach(hikeId => {
        if(!isEmpty(state.kmlData[hikeId])){
            const { LineString, Point } = state.kmlData[hikeId];
            if(!isEmpty(LineString)){
                bounds.corner1 = LineString[0].bounds._southWest
                bounds.corner2 = LineString[0].bounds._northEast
            }
        }
    })

    return {
        ...state,
        bounds
    }
};

const removeItem = (state, action) => {
    if(state.isUpdating){
        return {
            ...cancelTripEdit(state, action),
            entities: {
                ...state.entities,
                services: {
                    ...state.entities.services,
                    [action.serviceId]: {
                        ...state.entities.services[action.serviceId],
                        steps: state.entities.services[action.serviceId].steps.map(step => {
                            if (step.itemId == action.itemId) {
                                return {
                                    ...step,
                                    updateStatus: 'deleted'
                                }
                            }
                            return step;
                        })
                    }
                }
            }
        }
    }
    return {
        ...cancelTripEdit(state, action),
        entities: {
            ...state.entities,
            services:  {
                ...state.entities.services,
                [action.serviceId]: {
                    ...state.entities.services[action.serviceId],
                    steps: state.entities.services[action.serviceId].steps.filter(step => step.itemId != action.itemId)
                }
            },
        }
    }
}

const removeService = (state, action) => {
    return {
        ...cancelTripEdit(state, action),
        entities: {
            ...state.entities,
            services:  {
                ...omit(state.entities.services, [action.serviceId])
            },
        }
    }
}

const displayStops = (state, action) => ({
    ...state,
    showStops: action.display
});

const loadSession = (state, action) => ({
    ...action.state.map
});

const receiveDraft = (state, action) => {
    let services = {}

    const reservationId = action.data.result;
    const reservation = action.data.entities.reservations[reservationId];

    let mergedServices = structuredClone(action.data.entities.services)
    let servicesId = Object.keys(mergedServices)
    if(!isEmpty(action.data.entities.pendingServices)) {
        Object.keys(action.data.entities.pendingServices).forEach(serviceId => {
            if (!servicesId.includes(serviceId)) {
                mergedServices[serviceId] = structuredClone(action.data.entities.pendingServices[serviceId])
            } else {
                mergedServices[serviceId].items = [ ...mergedServices[serviceId].items, ...action.data.entities.pendingServices[serviceId].items]
            }
        })
    }

    const mergedItems =  {
        ...action.data.entities.items,
        ...action.data.entities.pendingItems,
    }
    Object.keys(mergedServices).forEach(serviceId => {
        const service = mergedServices[serviceId]
        let newSteps = []
        service.items.forEach(itemId => {
            const item = mergedItems[itemId]
            let fromTo = denormalizeFromToItems(service.type, item)

            newSteps.push({
                itemId,
                ...fromTo
            })
        })

        services[serviceId] = {
            ...service,
            steps: newSteps
        }
    })

    return{
        ...state,
        isUpdating: reservation.status != 'draft',
        entities: {
            ...state.entities,
            ...action.data.entities,
            services,
            items: mergedItems
        },
        trip: tripInitialState
    }
};

const addServiceFromSession = (state, action) => {
    let newSteps = []
    let itemIds = []
    action.service.items.forEach(item => {
        let fromTo = denormalizeFromToItems(action.service.type, item)
        itemIds.push(item.id)
        newSteps.push({
            itemId: item.id,
            ...fromTo
        })
    })
    return {
        ...state,
        entities: {
            ...state.entities,
            services: {
                ...state.entities.services,
                [action.service.id]: {
                    ...action.service,
                    items: itemIds,
                    steps: newSteps
                }
            }
        }
    }
}

const denormalizeFromToItems = (serviceType, item) => {
    let from = null, to = null;

    switch(serviceType) {
        case CAR_PARKING :
            if(item.place.id){
                from = item.place
                to = item.place
            } else {
                from = {
                    ...item.place,
                    step: {
                        id: getDefaultStepId(),
                        ...item.place.stop
                    },
                }

                to = from
            }
            break;
        case CAR_TRANSPORT :
            if(item.collectionPlace.id){
                from = item.collectionPlace
            }
            if(item.depositPlace.id){
                to = item.depositPlace
            }
            break;
        default:
            from = item.from
            to = item.to
    }
    return {
        from,
        to
    }
}

const unload = (state, action) => ({
    ...initialState
});

const preSelectHike = (state, action) => ({
    ...state,
    hike: action.hike
});

const setDirection = (state, action) => ({
    ...state,
    direction: action.direction
});

export default createReducer(initialState, {
    [actions.REQUEST_STOPS]: requestStops,
    [actions.RECEIVE_STOPS]: receiveStops,
    [actions.REQUEST_PLAN_STEPS]: requestPlanSteps,
    [actions.RECEIVE_PLAN_STEPS]: receivePlanSteps,
    [actions.MOSE_HOVER_STOP]: moseOverStop,
    [actions.SELECT_DEPARTURE_STEP]: selectDepartureStep,
    [actions.SELECT_ARRIVAL_STEP]: selectArrivalStep,
    [actions.SET_KML_DATA]: setKmlData,
    [actions.UPDATE_FILTERS]: updateFilters,
    [actions.SAVE_STEP]: saveStep,
    [actions.NEW_STEP]: newStep,
    [actions.ADD_STEP]: addStep,
    [actions.EDIT_STEP]: editStep,
    [actions.DELETE_STEP]: deleteStep,
    [actions.SELECT_STEP]: selectStep,
    [actions.SELECT_STOP]: selectStop,
    [actions.SAVE_STOP]: saveStop,
    [actions.TOGGLE_SUMMARY]: toggleSummary,
    [actions.MOVE_HIKER_MARKER]: moveHikerMarker,
    [actions.SELECT_HIKE]: selectHike,
    [actions.MOSE_OVER_HIKE]: moseOverHike,
    [actions.SELECT_THEME]: selectTheme,
    [actions.START_TRIP_EDIT]: startTripEdit,
    [actions.CANCEL_TRIP_EDIT]: cancelTripEdit,
    [actions.CHANGE_MAP_LAYER]: changeMapLayer,
    [actions.UPDATE_BOUNDS]: updateBounds,
    [actions.REQUEST_THEMES]: requestThemes,
    [actions.RECEIVE_THEMES]: receiveThemes,
    [actions.REQUEST_HIKE_STEPS]: requestHikeSteps,
    [actions.RECEIVE_HIKE_STEPS]: receiveHikeSteps,
    [actions.DISPLAY_STOPS]: displayStops,
    [actions.PRESELECT_HIKE]: preSelectHike,
    [actions.SET_DIRECTION]: setDirection,
    [actions.ADD_SERVICE_FROM_SESSION]: addServiceFromSession,

    [book.SUBMIT_SERVICE]: saveService,
    [book.SUBMIT_SERVICE_ITEM]: saveStep,
    [book.UPDATE_SERVICE_ITEM]: saveStep,
    [book.TOGGLE_EDIT_SERVICE_ITEM]: editStep,
    [book.ADD_SERVICE_DATA]: clearTheme,
    [book.REQUEST_SERVICE_DATA]: saveServiceType,
    [book.ADD_SERVICE_ITEM]: addItem,
    [book.CANCEL]: cancelTripEdit,
    [book.REMOVE_SERVICE]: removeService,
    [book.REMOVE_SERVICE_ITEM]: removeItem,
    [book.SHOW_ADD_SERVICE]: cancelTripEdit,
    [book.LOAD_SESSION]: loadSession,
    [book.RECEIVE_DRAFT_QUOTE]: receiveDraft,
    [book.RECEIVE_HIKES]: receiveHikes,
    [book.REQUEST_THEMES]: requestThemes,
    [book.RECEIVE_THEMES]: receiveThemes,
    [book.UNLOAD]: unload,
});
